/**
 * Horizontal edge-fade for a scrollable row. Returns a `mask-image` value that
 * fades whichever side still has hidden overflow — so it's implicit there's
 * more content off-screen instead of a hard cut-off. Returns `undefined` when
 * the content fits (no overflow → no fade, hard edges are fine).
 *
 * Pure + DOM-free so the geometry is exhaustively unit-testable; the component
 * (`FadingScrollRow`) supplies the live scroll metrics.
 */
export interface EdgeFadeInput {
  scrollLeft: number
  clientWidth: number
  scrollWidth: number
  /** Fade ramp width in px (default 16). */
  fadePx?: number
}

export function edgeFadeMask({
  scrollLeft,
  clientWidth,
  scrollWidth,
  fadePx = 16,
}: EdgeFadeInput): string | undefined {
  // 1px slack absorbs sub-pixel rounding so a fully-scrolled row doesn't keep a
  // permanent hairline fade at the pinned edge.
  const EPS = 1
  const fadeLeft = scrollLeft > EPS
  const fadeRight = scrollLeft + clientWidth < scrollWidth - EPS
  if (!fadeLeft && !fadeRight) return undefined
  const start = fadeLeft ? `transparent 0, #000 ${fadePx}px` : '#000 0'
  const end = fadeRight ? `#000 calc(100% - ${fadePx}px), transparent 100%` : '#000 100%'
  return `linear-gradient(to right, ${start}, ${end})`
}
